import React from 'react';
import { Tag, seg } from '../tags/Tag';
import type { TextCellProps } from './types';

/**
 * TextCell - Renders plain text values
 * 
 * Features:
 * - Handles null/undefined with em dash (—)
 * - CSS ellipsis truncation
 * - Title attribute for full text on hover
 * - Optional icon on the right (always visible or hidden)
 */
export const TextCell: React.FC<TextCellProps> = ({ value, config }) => {
  // Handle null/undefined/empty values
  if (value === null || value === undefined || value === '') {
    return <span className="table-cell-text">—</span>;
  }
  
  // Convert to string for display
  const displayValue = String(value);
  
  // If icon is configured and should be visible
  const showIcon = config?.icon && config?.iconVisibility === 'always';
  
  const isDisabled = config?.disabled;
  
  // Determine text color and weight
  const colorMode = config?.color || 'default';
  const textColor = isDisabled 
    ? 'var(--core-text-disabled)' 
    : colorMode === 'strong' 
      ? 'var(--core-text-primary)'   // strong = primary (darker)
      : 'var(--core-text-secondary)'; // default = secondary (lighter)
  const fontWeight = config?.fontWeight || 400;  // Default: 400 (normal)
  
  if (showIcon) {
    const Icon = config!.icon!;
    return (
      <div 
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          overflow: 'hidden',
          opacity: isDisabled ? 0.5 : 1,
          color: textColor  // Apply to container so icon inherits
        }}
      >
        <span 
          className={`table-cell-text ${config?.className || ''}`}
          title={displayValue}
          style={{
            fontWeight,
            fontSize: '0.875rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0
          }}
        >
          {displayValue}
        </span>
        <Tag
          template="action-sm"
          segments={[seg.icon(Icon, { size: 16 })]}
          onClick={config?.onClick}
          disabled={isDisabled}
          style={{ color: 'inherit' }}
        />
      </div>
    );
  }
  
  return (
    <span 
      className={`table-cell-text ${config?.className || ''}`}
      title={displayValue}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        color: textColor,
        fontWeight,
        fontSize: '0.875rem',
        opacity: isDisabled ? 0.5 : 1
      }}
    >
      {displayValue}
    </span>
  );
};

